import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft, FiEye, FiFileText, FiTruck, FiXCircle } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import { getItemsCached, getLocationsCached, getEmployeesCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { formatAmount } from '../../utils/documentNumbering';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import { useLocation } from 'react-router-dom';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ItemSelect from '../../components/ItemSelect';
import SerialSelectPopover from '../../components/SerialSelectPopover';
import {
  createTransaction, updateTransaction, deleteTransaction, updateTransactionStatus
} from '../../utils/transactionHelper';
import { generateStockFlow, deleteStockFlow, getAvailableBatches, getAvailableSerials, getItemStockCached, invalidateItemStock, listStockFlowRows, applyEntriesToStockCache } from '../../utils/stockFlowHelper';
import { costRowsChange } from '../../utils/itemCostEngine';
import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';

const TRAN_TYPE_CODE = 'SND';

// INV-15: save the document and the rows `addRows` adds in ONE commit
// (transactionHelper runs it inside its own save).
const saveWithRows = async (save, addRows) => {
  // The helper always runs extraWrites inside its save. If it did not here,
  // this call was merged into a save already running (double click) — that
  // save added the rows, so nothing is added again.
  return save({ extraWrites: (w, ctx) => addRows(w, ctx) });
};

// INV-15: the document's stock rows, added to `writer`. Its old rows (if any) are
// replaced in the same commit, and the items' running averages move with them
// (old rows out, new rows in) — so a retry never moves stock twice.
const replaceStockRows = async (companyId, writer, { transactionId, stock, removeOld, financialYearId, userId }) => {
  const [oldRows, newRows] = await Promise.all([
    removeOld ? listStockFlowRows(companyId, transactionId) : Promise.resolve([]),
    stock ? generateStockFlow(companyId, stock, userId, { writer }) : Promise.resolve([]),
  ]);
  const kept = new Set(newRows.map(r => r.id));
  oldRows.forEach(r => { if (!kept.has(r.id)) writer.delete(doc(db, 'companies', companyId, 'stockFlow', r.id)); });
  if (oldRows.length) writer.afterCommit(() => { applyEntriesToStockCache(companyId, oldRows, -1); invalidateItemStock(companyId); });
  try {
    await costRowsChange(companyId, writer, { added: newRows, removed: oldRows, financialYearId });
  } catch (e) {
    console.warn('[stock] item average not updated (Data Repair will rebuild it):', e && e.message);
  }
  return newRows;
};

// INV-15: a new document keeps ONE pre-generated id until it is saved (`pend`),
// so a retry after a dropped connection can never save it twice — if the earlier
// attempt did reach the server, that saved document is returned instead.
const savedEarlier = async (companyId, pend) => {
  if (!pend.tried) { pend.tried = true; return null; }
  const snap = await getDoc(doc(db, 'companies', companyId, 'transactions', pend.id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const SUMMARY_SPEC = { statuses: ['Draft', 'Sent', 'Partially Received', 'Received'] };

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', unitCost: 0, lineTotal: 0, trackingType: 'None', batchNumber: '', serialNumbers: [] };
const STATUS_COLORS = { Draft: colors.warning.main, Sent: colors.info.main, 'Partially Received': '#FF8C00', Received: colors.success.main, Cancelled: colors.error.main };

const SendInventory = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch, branches } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'stockTransfer');
  const location = useLocation();
  const branchList = useMemo(() => (branches || []).filter(b => b.id !== 'all'), [branches]);
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records, loading, refresh: loadRecords, ensureDoc, applyLocal, summary: listSummary, pagination
  } = useDocListPagination({ tranTypeCode: TRAN_TYPE_CODE, fullMode: listFiltering, summarySpec: SUMMARY_SPEC });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    fromBranchId: '', fromBranchName: '', fromLocationId: '', fromLocationName: '',
    toBranchId: '', toBranchName: '', toLocationId: '', toLocationName: '',
    notes: '', status: 'Draft', employeeId: '', employeeName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});
  const [availableSerials, setAvailableSerials] = useState({});
  const [employees, setEmployees] = useState([]);
  const [stockBalances, setStockBalances] = useState({});
  const newDocRef = useRef(null); // INV-15: { id, tried } of the new document being saved

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true).sort((a, b) => (a.locationName || '').localeCompare(b.locationName || '')));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: formData.fromBranchId || ((activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined)
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch, formData.fromBranchId]);

  useEffect(() => { loadItems(); loadLocations(); loadEmployees(); }, [loadItems, loadLocations, loadEmployees]);

  // Default the source branch to the user's active branch.
  useEffect(() => {
    if (!formData.fromBranchId && activeBranch && activeBranch.id !== 'all') {
      setFormData(prev => ({ ...prev, fromBranchId: activeBranch.id, fromBranchName: activeBranch.branchName || '' }));
    }
  }, [activeBranch, formData.fromBranchId]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.docNumber?.toLowerCase().includes(q)
        || r.fromLocationName?.toLowerCase().includes(q) || r.toLocationName?.toLowerCase().includes(q)
        || r.toBranchName?.toLowerCase().includes(q));
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => ({ ...line, unitPrice: line.unitCost || 0, lineTotal: Math.round((Number(line.quantity) || 0) * (Number(line.unitCost) || 0) * 100) / 100 });
  const totals = useMemo(() => ({ totalValue: Math.round(lineItems.map(calculateLine).reduce((s, l) => s + l.lineTotal, 0) * 100) / 100 }), [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const setBranch = (which, branchId) => {
    const b = branchList.find(x => x.id === branchId);
    if (which === 'from') setFormData(prev => ({ ...prev, fromBranchId: branchId, fromBranchName: b?.branchName || '' }));
    else setFormData(prev => ({ ...prev, toBranchId: branchId, toBranchName: b?.branchName || '' }));
  };
  const setLoc = (which, locationId) => {
    const loc = locations.find(l => l.id === locationId);
    if (which === 'from') setFormData(prev => ({ ...prev, fromLocationId: locationId, fromLocationName: loc?.locationName || '' }));
    else setFormData(prev => ({ ...prev, toLocationId: locationId, toLocationName: loc?.locationName || '' }));
  };

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev]; updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure, unitCost: patch.unitCost };
        }
      }
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure, unitCost: seeded.unitCost };
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          if (item.trackingType === 'Batch' && formData.fromLocationId) {
            getAvailableBatches(activeCompany.id, value, formData.fromLocationId).then(batches => {
              setAvailableBatches(prev2 => ({ ...prev2, [value]: batches }));
              if (batches.length > 0) {
                setLineItems(prev2 => {
                  const u = [...prev2];
                  if (u[index] && u[index].itemId === value) u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial' && formData.fromLocationId) {
            getAvailableSerials(activeCompany.id, value, formData.fromLocationId).then(serials => {
              setAvailableSerials(prev2 => ({ ...prev2, [value]: serials }));
            });
          }
        }
      }
      updated[index] = calculateLine(updated[index]); return updated;
    });
  };
  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current; pendingFocusRowRef.current = null;
    setTimeout(() => { const el = lineItemRefs.current[rowIdx]; if (el && typeof el.focus === 'function') el.focus(); }, 50);
  }, [lineItems.length]);
  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return; e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    if (!line.itemId && !(Number(line.quantity) > 0)) return;
    if (isLastRow) { if (window.confirm('Add a new line item?')) { pendingFocusRowRef.current = lineItems.length; addLine(); } }
    else { const next = lineItemRefs.current[index + 1]; if (next && typeof next.focus === 'function') next.focus(); }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      fromBranchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : '', fromBranchName: (activeBranch && activeBranch.id !== 'all') ? (activeBranch.branchName || '') : '',
      fromLocationId: '', fromLocationName: '', toBranchId: '', toBranchName: '', toLocationId: '', toLocationName: '',
      notes: '', status: 'Draft', employeeId: '', employeeName: ''
    });
    setLineItems([{ ...EMPTY_LINE }]); setEditingRecord(null);
    newDocRef.current = null;
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({
      docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0],
      fromBranchId: rec.fromBranchId || '', fromBranchName: rec.fromBranchName || '',
      fromLocationId: rec.fromLocationId || '', fromLocationName: rec.fromLocationName || '',
      toBranchId: rec.toBranchId || '', toBranchName: rec.toBranchName || '',
      toLocationId: rec.toLocationId || '', toLocationName: rec.toLocationName || '',
      notes: rec.notes || '', status: rec.status || 'Draft', employeeId: rec.employeeId || '', employeeName: rec.employeeName || ''
    });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) { setViewingRecord(rec); setViewMode('view'); window.history.replaceState({}, document.title); }
    });
  }, [location.state, ensureDoc]);

  // Stock lines for the OUT leg — single leg at (source branch, source location).
  const buildStockFlowLines = (calculatedLines) => calculatedLines.map(l => ({
    ...l,
    direction: 'out',
    locationId: formData.fromLocationId,
    locationName: formData.fromLocationName,
    branchId: formData.fromBranchId || null,
    branchName: formData.fromBranchName || null,
    batchNumber: l.batchNumber || '',
    serialNumbers: l.serialNumbers || []
  }));

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.fromLocationId || !formData.toLocationId) { alert('Please select both source and destination locations.'); return; }
    if (formData.fromBranchId === formData.toBranchId && formData.fromLocationId === formData.toLocationId) {
      alert('Source and destination cannot be the same branch + location.'); return;
    }
    const validLines = lineItems.filter(l => l.itemId && Number(l.quantity) > 0);
    if (validLines.length === 0) { alert('Please add at least one item.'); return; }
    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) { alert(`Batch selection is required for "${line.itemName || line.description}".`); return; }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        if (serials.length !== (Number(line.quantity) || 0)) { alert(`"${line.itemName || line.description}" requires exactly ${line.quantity} serial number(s). You selected ${serials.length}.`); return; }
      }
    }
    if (!activeFinancialYear?.id) { alert('No active financial year.'); return; }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        return { ...calculateLine(l), lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
      });
      const status = saveStatus || 'Draft';
      const data = {
        docDate: formData.docDate,
        fromBranchId: formData.fromBranchId || null, fromBranchName: formData.fromBranchName || null,
        fromLocationId: formData.fromLocationId, fromLocationName: formData.fromLocationName,
        toBranchId: formData.toBranchId || null, toBranchName: formData.toBranchName || null,
        toLocationId: formData.toLocationId, toLocationName: formData.toLocationName,
        // legacy/report compatibility
        fromLocation: formData.fromLocationName, toLocation: formData.toLocationName,
        locationId: formData.fromLocationId, locationName: formData.fromLocationName,
        notes: formData.notes, status, totalValue: totals.totalValue, lines: calculatedLines,
        employeeId: formData.employeeId, employeeName: formData.employeeName,
        // received tracking (updated by Receive Inventory)
        receivedQty: editingRecord?.receivedQty || 0,
        financialYearId: activeFinancialYear.id,
        branchId: formData.fromBranchId || activeBranch?.id || null,
        branchName: formData.fromBranchName || activeBranch?.branchName || null
      };

      // Post the OUT leg when Sent (deduct source). Draft posts nothing. INV-15:
      // the rows (and the removal of an earlier version's rows) go in the SAME
      // save as the document — never a "Sent" document with no stock moved.
      const hadRows = !!editingRecord && (editingRecord.status === 'Sent' || editingRecord.status === 'Partially Received' || editingRecord.status === 'Received');
      const addRows = (status === 'Sent' || hadRows) ? (w, ctx) => replaceStockRows(activeCompany.id, w, {
        transactionId: ctx.transactionId, removeOld: !!editingRecord, financialYearId: activeFinancialYear.id, userId: currentUser.uid,
        stock: status === 'Sent' ? {
          transactionId: ctx.transactionId, tranTypeCode: TRAN_TYPE_CODE, docNumber: ctx.docNumber,
          docDate: formData.docDate, financialYearId: activeFinancialYear.id,
          branchId: formData.fromBranchId || null, branchName: formData.fromBranchName || null,
          status: 'Sent', locationId: formData.fromLocationId, locationName: formData.fromLocationName, lines: buildStockFlowLines(calculatedLines)
        } : null
      }) : null;

      let savedDocId, savedDocNumber, newTranData = null;
      if (editingRecord) {
        if (addRows) await saveWithRows((opts) => updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid, opts), addRows);
        else await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        savedDocId = editingRecord.id; savedDocNumber = editingRecord.docNumber;
      } else {
        if (!newDocRef.current) newDocRef.current = { id: doc(collection(db, 'companies', activeCompany.id, 'transactions')).id, tried: false };
        const pend = newDocRef.current;
        const earlier = await savedEarlier(activeCompany.id, pend);
        if (earlier && earlier.status !== status) {
        // The earlier attempt was saved, but as something else (e.g. a Draft)
        // — don't pretend this one went through; let the user open it.
        alert(`${earlier.docNumber} was already saved as ${earlier.status} (the connection dropped before the confirmation arrived). Open it from the list to continue.`);
        setViewMode('list'); resetForm();
        loadRecords().catch(err => console.error('Background refresh failed:', err));
        return;
      }
      if (earlier) alert(`${earlier.docNumber} was already saved (the connection dropped before the confirmation arrived) — it was not saved again.`);
        const tran = { ...data, tranTypeCode: TRAN_TYPE_CODE };
        newTranData = earlier || (addRows
          ? await saveWithRows((opts) => createTransaction(activeCompany.id, tran, currentUser.uid, { ...opts, docId: pend.id }), addRows)
          : await createTransaction(activeCompany.id, tran, currentUser.uid, { docId: pend.id }));
        savedDocId = newTranData.id; savedDocNumber = newTranData.docNumber;
      }

      const savedRecord = editingRecord ? { ...editingRecord, ...data, id: savedDocId, status } : { ...newTranData, status };
      applyLocal(prev => editingRecord ? prev.map(r => r.id === savedDocId ? savedRecord : r) : [savedRecord, ...prev]);
      invalidateItemStock(activeCompany.id);
      setViewMode('list'); resetForm();
      loadRecords().catch(err => console.error('Background refresh failed:', err));
      if (status === 'Sent') alert(`Sent. Give this Transfer No to the receiver: ${savedDocNumber}`);
    } catch (error) { console.error('Error:', error); alert('Error saving: ' + error.message); } finally { setSaving(false); }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, rec.id); await loadRecords();
    } catch (error) { console.error('Error:', error); alert('Error deleting: ' + error.message); }
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to cancel.'); return; }
    if (rec.status === 'Cancelled') { alert('Already cancelled'); return; }
    if (rec.status === 'Received' || rec.status === 'Partially Received') { alert('This transfer has already been (partially) received. Cancel the Receive first.'); return; }
    if (rec.status === 'Sent') { if (!window.confirm('This will return the sent stock to the source. Continue?')) return; }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      if (rec.status === 'Sent') await deleteStockFlow(activeCompany.id, rec.id);
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      invalidateItemStock(activeCompany.id); await loadRecords();
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const branchLoc = (branchName, locName) => `${branchName ? branchName + ' / ' : ''}${locName || '-'}`;

  const renderListView = () => (
    <>
      <ActionBar><SearchBox><FiSearch /><input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></SearchBox>
        <FilterGroup><StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="All">All Status</option><option value="Draft">Draft</option><option value="Sent">Sent</option><option value="Partially Received">Partially Received</option><option value="Received">Received</option><option value="Cancelled">Cancelled</option>
        </StatusFilterSelect>{canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Send</TranAddButton>}</FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Send Inventory documents</h3><p>{records.length === 0 ? 'Create your first send' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Send</TranAddButton>}</TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Transfer No</SortableTh><SortableTh field="docDate">Date</SortableTh>
          <Th>From (branch / location)</Th><Th>To (branch / location)</Th>
          <SortableTh field="totalValue" style={{ textAlign: 'right' }}>Value</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh><Th style={{ textAlign: 'center' }}>Actions</Th></tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}><Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              <Td>{branchLoc(rec.fromBranchName, rec.fromLocationName)}</Td><Td>{branchLoc(rec.toBranchName, rec.toLocationName)}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && rec.status === 'Draft' && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                {canDelete && rec.status !== 'Cancelled' && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && (rec.status === 'Draft' || rec.status === 'Sent') && <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}><FiXCircle /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}</tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar><span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong></span><span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Sent: <strong>{listSummary.counts.Sent || 0}</strong> | Received: <strong>{listSummary.counts.Received || 0}</strong></span></SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null; const rec = viewingRecord;
    return (<>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
        <FormActions>{canChange && rec.status === 'Draft' && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}</FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Send Details</SectionTitle><InfoGrid>
        <InfoItem><label>Transfer No</label><span>{rec.docNumber}</span></InfoItem><InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
        <InfoItem><label>From</label><span>{branchLoc(rec.fromBranchName, rec.fromLocationName)}</span></InfoItem>
        <InfoItem><label>To</label><span>{branchLoc(rec.toBranchName, rec.toLocationName)}</span></InfoItem>
        <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
      </InfoGrid></FormCard>
      <FormCard><SectionTitle>Items</SectionTitle><TableContainer><Table><thead><tr><Th>#</Th><Th>Item</Th><Th style={{ textAlign: 'right' }}>Qty</Th><Th>Batch</Th><Th>Serials</Th></tr></thead><tbody>
        {(rec.lines || []).map((line, i) => (<Tr key={i}><Td>{i + 1}</Td><Td>{line.itemName || line.description}</Td><Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td><Td>{line.batchNumber || '-'}</Td><Td>{(line.serialNumbers || []).join(', ') || '-'}</Td></Tr>))}
      </tbody></Table></TableContainer></FormCard>
      {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
    </>);
  };

  const renderFormView = () => (
    <>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Send Inventory'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && <SaveButton $approve onClick={() => handleSave('Sent')} disabled={saving}><FiTruck /> Send (deduct source)</SaveButton>}
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Send Details</SectionTitle><FormGrid>
        <FormGroup><Label>Transfer No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
        <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
        <FormGroup><Label>From Branch</Label>
          <Select value={formData.fromBranchId} onChange={e => setBranch('from', e.target.value)}>
            <option value="">-- Source Branch --</option>
            {branchList.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
          </Select>
        </FormGroup>
        <FormGroup><Label>From Location *</Label>
          <Select value={formData.fromLocationId} onChange={e => setLoc('from', e.target.value)}>
            <option value="">-- Source Location --</option>
            {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.locationCode ? loc.locationCode + ' - ' : ''}{loc.locationName}</option>)}
          </Select>
        </FormGroup>
        <FormGroup><Label>To Branch</Label>
          <Select value={formData.toBranchId} onChange={e => setBranch('to', e.target.value)}>
            <option value="">-- Destination Branch --</option>
            {branchList.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
          </Select>
        </FormGroup>
        <FormGroup><Label>To Location *</Label>
          <Select value={formData.toLocationId} onChange={e => setLoc('to', e.target.value)}>
            <option value="">-- Destination Location --</option>
            {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.locationCode ? loc.locationCode + ' - ' : ''}{loc.locationName}</option>)}
          </Select>
        </FormGroup>
        <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
        <FormGroup><Label>Employee</Label>
          <Select value={formData.employeeId} onChange={e => { const emp = employees.find(em => em.id === e.target.value); handleHeaderChange('employeeId', e.target.value); handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : ''); }}>
            <option value="">-- Select Employee --</option>
            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>)}
          </Select>
        </FormGroup>
      </FormGrid></FormCard>
      <FormCard>
        <LineItemsHeader><SectionTitle style={{ margin: 0 }}>Items</SectionTitle><AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton></LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50 }}>#</LineTh><LineTh style={{ width: 240 }}>Item</LineTh><LineTh style={{ width: 130 }}>Qty</LineTh><LineTh style={{ width: 140 }}>Unit Cost</LineTh><LineTh style={{ width: 140 }}>Total</LineTh>
          {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160 }}>Batch</LineTh>}
          {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220 }}>Serial Numbers</LineTh>}
          <LineTh style={{ width: 50 }}></LineTh></tr></thead><tbody>
          {lineItems.map((line, index) => { const calc = calculateLine(line); return (
            <tr key={index}><LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
              <LineTd><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /><LineUnitPicker item={items.find(i => i.id === line.itemId)} line={line} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} /></div></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.unitCost} onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
              {lineItems.some(l => l.trackingType === 'Batch') && (
                <LineTd>{line.trackingType === 'Batch' ? (
                  <select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120, border: '1px solid #ddd', borderRadius: '4px' }}>
                    <option value="">Select Batch</option>
                    {(availableBatches[line.itemId] || []).map(b => <option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining})</option>)}
                  </select>
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
              )}
              {lineItems.some(l => l.trackingType === 'Serial') && (
                <LineTd>{line.trackingType === 'Serial' ? (
                  <SerialSelectPopover availableSerials={availableSerials[line.itemId] || []} selectedSerials={line.serialNumbers || []} requiredCount={line.quantity || 0} onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)} />
                ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
              )}
              <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
            </tr>); })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody><TotalRow><td>Total Value ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalValue)}</td></TotalRow></tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard><SectionTitle>Notes</SectionTitle><FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid></FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Sent'); },
    viewMode, saving
  });

  return (
    <PageLayout pageTitle="Send Inventory" breadcrumb="Store / Send Inventory">
      {viewMode === 'list' && renderListView()}{viewMode === 'form' && renderFormView()}{viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default SendInventory;
